import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "@store/index";
import {
  CargoPackagingProps,
  CargoPackagingResponse,
  RequestPackingParams,
} from "./types";
import {
  exportPackingListApi,
  getPackingApi,
  loadFileApi,
  updatePackingApi,
} from "./api";
import {
  getErrorMessageFromBlobResponse,
  getErrorMessageFromResponse,
} from "@helpers/get-error-message";

type PackingState = CargoPackagingResponse & {
  loading: boolean;
  error: string | null;
  success: string | null;
  currentParams: Partial<RequestPackingParams> | null;
} & {
  uploading: {
    loading: boolean;
    error: string | null;
    success: string | null;
  };
} & {
  exporting: {
    loading: boolean;
    error: string | null;
  };
};

const initialState: PackingState = {
  items: null,
  meta: null,
  loading: false,
  error: null,
  success: null,
  currentParams: null,
  uploading: {
    loading: false,
    error: null,
    success: null,
  },
  exporting: {
    loading: false,
    error: null,
  },
  total: 0,
  page: 0,
  size: 0,
};

const packingSlice = createSlice({
  name: "packing",
  initialState,
  reducers: {
    getStart(state: PackingState) {
      state.loading = true;
      state.error = null;
    },
    createSuccess(
      state: PackingState,
      action: PayloadAction<{ success: string }>,
    ) {
      const { success } = action.payload;
      state.success = success;
      state.loading = false;
      state.error = null;
    },
    getFailure(state: PackingState, action: PayloadAction<{ error: string }>) {
      const { error } = action.payload;
      state.loading = false;
      state.uploading.loading = false;
      state.error = error;
      state.exporting.loading = false;
    },
    getPackingSuccess(
      state: PackingState,
      action: PayloadAction<
        CargoPackagingResponse & { params?: RequestPackingParams }
      >,
    ) {
      const { items, total, page, size, meta, params } = action.payload;
      state.items = items;
      state.meta = meta;
      state.total = total;
      state.page = page;
      state.size = size;
      state.loading = false;
      if (params) {
        const { page, size, ...exportParams } = params;
        state.currentParams = exportParams;
      }
    },
    setPackingList(
      state: PackingState,
      action: PayloadAction<{ id: number; data: Partial<CargoPackagingProps> }>,
    ) {
      const { id, data } = action.payload;
      if (!state.items) return;

      const idx = state.items.findIndex((item) => item.id === id);
      if (idx !== -1) {
        state.items[idx] = {
          ...state.items[idx],
          ...data,
        };
      }
    },
    cleanData(state: PackingState) {
      state.items = null;
    },
    hideWarning(state: PackingState) {
      state.error = null;
      state.success = null;
      state.uploading.error = null;
      state.uploading.success = null;
    },
    getFileUploadingStart(state: PackingState) {
      state.uploading.error = null;
      state.uploading.success = null;
      state.uploading.loading = true;
    },
    getFileUploadingSuccess(
      state: PackingState,
      action: PayloadAction<string>,
    ) {
      state.uploading.success = action.payload;
      state.uploading.loading = false;
    },
    startExport(state: PackingState) {
      state.exporting = {
        loading: true,
        error: null,
      };
    },
    successExport(state: PackingState) {
      state.exporting.loading = false;
      state.exporting.error = null;
    },
  },
});

export const {
  getStart,
  getPackingSuccess,
  setPackingList,
  getFailure,
  cleanData,
  createSuccess,
  hideWarning,
  getFileUploadingStart,
  getFileUploadingSuccess,
  startExport,
  successExport,
} = packingSlice.actions;

export const { actions, reducer } = packingSlice;
export default packingSlice.reducer;

export const fetchPacking =
  ({ id, params }: { id: number; params: RequestPackingParams }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart());
      const data = await getPackingApi(id, params);
      dispatch(getPackingSuccess({ ...data, params }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ error }));
    }
  };

export const updatePackingList =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<CargoPackagingProps>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart());
      const data = await updatePackingApi(id, params);
      dispatch(
        setPackingList({ id, data: { ...data, warehouse: params.warehouse } }),
      );
      dispatch(
        createSuccess({
          success: "Packing list updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ error }));
    }
  };

export const exportPackingList =
  (id: number, params?: Partial<RequestPackingParams>): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport());
      const { file, filename } = await exportPackingListApi(id, params);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch (err) {
      const error = await getErrorMessageFromBlobResponse(err);
      dispatch(
        getFailure({
          error,
        }),
      );
    }
  };

export const preloadFileByType =
  (data: File, id: number, size: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getFileUploadingStart());
      await loadFileApi(data, id);
      dispatch(fetchPacking({ id, params: { page: 1, size } }));
      dispatch(getFileUploadingSuccess("File uploaded successfully"));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(
        getFailure({
          error,
        }),
      );
    }
  };
